// A CARD CONFIRM THAT CANNOT HANG. Kept free of imports so it runs under
// `node --test` (confirm-watch.test.mjs).
//
// 12 Oct 2026, replay: at 12:21:33 a Capital One 3DS challenge opened over
// the page, the customer went to their bank app and back twice, and then
// nothing: stripe.confirmSetup never settled, the button said "Just a sec…"
// for 20 minutes, and Stripe later recorded setup_intent_authentication_failure.
// So while a confirm runs we also ask Stripe for the SetupIntent itself: a few
// seconds in (did a 3DS check start?), whenever the page comes back into view,
// and every half minute from the first minute on. Whatever answers first wins:
//   succeeded                         the card is saved: go to the success page
//   requires_payment_method (15 s+)   the check failed: say so, reset
//   requires_action / processing      still with the bank: keep waiting, and
//                                     after 30 s or a trip away say so
// Stripe's statuses: docs.stripe.com/payments/intents#intent-statuses
// (SetupIntent: requires_payment_method, requires_confirmation,
// requires_action, processing, canceled, succeeded).

export const FIRST_CHECK_MS = 5000
export const WATCH_FROM_MS = 60000
export const WATCH_EVERY_MS = 30000
// Before this, requires_payment_method may just mean the confirm has not
// reached Stripe yet (a reused SetupIntent still carries the last error).
export const FAIL_AFTER_MS = 15000
export const WAITING_AFTER_MS = 30000

export const WAITING_FOR_BANK = "Waiting for your bank to confirm. If you've approved it in your banking app, this will update."

// confirm(): stripe.confirmSetup(...)'s promise. retrieve(): resolves
// { setupIntent } (stripe.retrieveSetupIntent(clientSecret)).
// Resolves once: { error } or { setupIntent }, with `via` ('confirm' |
// 'check'), `threeDs` (a 3DS check was seen), `leftPage`, `seconds`.
export const watchConfirm = ({
  confirm,
  retrieve,
  doc = globalThis.document,
  now = () => Date.now(),
  setTimer = (fn, ms) => setTimeout(fn, ms),
  clearTimer = id => clearTimeout(id),
  onThreeDsStarted = () => {},
  onWaiting = () => {}
}) => new Promise(resolve => {
  const start = now()
  let done = false
  let threeDs = false
  let leftPage = false
  let waitingShown = false
  let timer = null

  const facts = via => ({ via, threeDs, leftPage, seconds: Math.round((now() - start) / 1000) })
  const finish = (result, via) => {
    if (done) return
    done = true
    if (timer !== null) clearTimer(timer)
    try { doc && doc.removeEventListener('visibilitychange', onVisibility) } catch (e) { /* no document */ }
    resolve({ ...result, ...facts(via) })
  }

  const check = async ({ cameBack = false } = {}) => {
    if (done) return
    let si = null
    try { si = (await retrieve())?.setupIntent || null } catch (e) { return }
    if (done || !si) return
    const elapsed = now() - start
    if (si.status === 'requires_action' && !threeDs) { threeDs = true; onThreeDsStarted() }
    if (si.status === 'succeeded') return finish({ setupIntent: si }, 'check')
    if (si.status === 'requires_payment_method' && elapsed >= FAIL_AFTER_MS) {
      const last = si.last_setup_error || {}
      return finish({
        error: { ...last, code: last.code || 'setup_intent_authentication_failure', type: last.type || 'invalid_request_error' }
      }, 'check')
    }
    if ((si.status === 'requires_action' || si.status === 'processing') && !waitingShown && (cameBack || elapsed >= WAITING_AFTER_MS)) {
      waitingShown = true
      onWaiting()
    }
  }

  const schedule = ms => {
    timer = setTimer(async () => {
      timer = null
      await check()
      if (!done) schedule(now() - start < WATCH_FROM_MS ? WATCH_FROM_MS - (now() - start) : WATCH_EVERY_MS)
    }, ms)
  }

  function onVisibility () {
    if (!doc) return
    if (doc.visibilityState === 'hidden') leftPage = true
    else check({ cameBack: true })
  }

  try { doc && doc.addEventListener('visibilitychange', onVisibility) } catch (e) { /* no document */ }
  schedule(FIRST_CHECK_MS)

  Promise.resolve()
    .then(confirm)
    .then(
      result => finish(result && result.error ? { error: result.error } : { setupIntent: result?.setupIntent || null }, 'confirm'),
      err => finish({ error: { message: err?.message, type: 'exception' } }, 'confirm')
    )
})

// threeds_result `status`: what came of a 3DS check, for PostHog.
export const threeDsStatus = result =>
  result.setupIntent ? 'succeeded' : result.error ? 'failed' : 'unknown'
